import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { GET_EVENT_DIVISION_ENTRIES } from "../../../../../../../graphql/eventDivision";
import { useRefocusEffect } from "../../../../../../../hooks/useRefocusEffect";

import { EntriesPageSkeleton } from "./EntriesPageSkeleton";
import { GroupedEntriesHeading } from "../../../../../../layout/entries/headings/GroupedEntriesHeading";
import { EntriesList } from "./EntriesList";
import { useEventDivision } from "../EventDivisionProvider";
import { useEvent } from "../../../EventProvider";
import { EmptyState } from "../../../../../../layout/EmptyState";
import { IconEmptySchedule } from "../../../../../../../styleguide/icons/icons";
import { useMeta } from "hooks/useMeta";
import { getEventDivisionMetadataDescription } from "utils/metadata";
import { useLocalizationContext } from "components/providers/LocalizationProvider";
import dayjs from "dayjs";
import { SHORT_DATE } from "utils/dates";

const EntriesPageWrapper = () => {
    const event = useEvent();
    const eventDivision = useEventDivision();
    const { translate } = useLocalizationContext();
    const eventDate = dayjs(event?.date).format(SHORT_DATE);

    const translationProps = {
        prefix: translate("See entries for"),
        eventName: event?.name,
        divisionName: eventDivision?.division.name,
        orgName: event?.organisation?.name,
        sportType: translate(event?.organisation?.sportType),
        eventDate: eventDate,
        eventLocation: event?.location?.placePredictionText,
    };
    useMeta({
        title: "event_division_entries_meta_title",
        description: getEventDivisionMetadataDescription(translationProps),
        translationProps,
        skip: !translationProps.eventName,
    });

    if (!event) return <EntriesPageSkeleton />;

    return !event.hideEntries ? (
        <LoadedEntriesPage />
    ) : (
        <EmptyState
            icon={<IconEmptySchedule />}
            text="The entries are top secret material. The Event Director has opted to keep the event entries hidden for now."
        />
    );
};
export default EntriesPageWrapper;

export const LoadedEntriesPage = () => {
    const { eventDivisionId } = useParams();
    const mainEventDivision = useEventDivision();

    const { data, refetch } = useQuery(GET_EVENT_DIVISION_ENTRIES, {
            variables: { id: eventDivisionId },
            fetchPolicy: "cache-and-network",
            nextFetchPolicy: "cache-first",
        }),
        eventDivision = data?.eventDivision;

    useRefocusEffect(refetch, true);

    if (!mainEventDivision || !eventDivision) return <EntriesPageSkeleton />;

    const isGroup = eventDivision.eventDivisions?.length > 0;
    const allEntries = isGroup ? eventDivision.eventDivisions : [eventDivision];

    return <EntriesPage allEntries={allEntries} isGroup={isGroup} />;
};

export const EntriesPage = ({ allEntries, isGroup, manageEntries }) => (
    <>
        {isGroup && (
            <GroupedEntriesHeading
                allEntries={allEntries}
                manageEntries={manageEntries}
            />
        )}

        {allEntries.map((eventDivision) => (
            <EntriesList
                key={eventDivision.id}
                eventDivision={eventDivision}
                isGroup={isGroup}
                manageEntries={manageEntries}
            />
        ))}
    </>
);
